import { useState } from 'react'
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi'

interface RangeCalendarProps {
  isOpen: boolean
  onClose: () => void
  onDateSelect: (startDate: Date | null, endDate: Date | null) => void
  selectedStartDate?: Date | null
  selectedEndDate?: Date | null
}

const RangeCalendar = ({ 
  isOpen, 
  onClose, 
  onDateSelect, 
  selectedStartDate, 
  selectedEndDate 
}: RangeCalendarProps) => {
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [startDate, setStartDate] = useState<Date | null>(selectedStartDate || null)
  const [endDate, setEndDate] = useState<Date | null>(selectedEndDate || null)
  const [hoverDate, setHoverDate] = useState<Date | null>(null)

  if (!isOpen) return null

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]

  const daysOfWeek = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
  }

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay()
  }

  const getPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))
  }

  const getNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))
  }

  const isDateInRange = (date: Date) => {
    if (!startDate || !endDate) return false
    return date >= startDate && date <= endDate
  }

  const isDateSelected = (date: Date) => {
    if (!startDate && !endDate) return false
    if (startDate && !endDate) {
      return date.toDateString() === startDate.toDateString()
    }
    return (
      date.toDateString() === startDate?.toDateString() ||
      date.toDateString() === endDate?.toDateString()
    )
  }

  const isDateInPartialRange = (date: Date) => {
    if (!startDate || endDate) return false
    if (hoverDate && hoverDate > startDate) {
      return date > startDate && date < hoverDate
    }
    if (hoverDate && hoverDate < startDate) {
      return date < startDate && date > hoverDate
    }
    return false
  }

  const handleDateClick = (date: Date) => {
    if (!startDate || (startDate && endDate)) {
      // Start new selection
      setStartDate(date)
      setEndDate(null)
    } else if (startDate && !endDate) {
      // Complete the range
      if (date > startDate) {
        setEndDate(date)
        onDateSelect(startDate, date)
      } else {
        setStartDate(date)
        setEndDate(null)
      }
    }
  }

  const handleDateHover = (date: Date) => {
    if (startDate && !endDate) {
      setHoverDate(date)
    }
  }

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth)
    const firstDay = getFirstDayOfMonth(currentMonth)
    const days: (Date | null)[] = []

    // Add previous month's trailing days
    const prevMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 0)
    const prevMonthDays = prevMonth.getDate()
    for (let i = firstDay - 1; i >= 0; i--) {
      days.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, prevMonthDays - i))
    }

    // Add current month's days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i))
    }

    // Add next month's leading days to fill the grid
    const remainingDays = 42 - days.length // 6 rows * 7 days
    for (let i = 1; i <= remainingDays; i++) {
      days.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, i))
    }

    return days.map((date, index) => {
      if (!date) return null

      const isCurrentMonth = date.getMonth() === currentMonth.getMonth()
      const isSelected = isDateSelected(date)
      const inRange = isDateInRange(date)
      const inPartialRange = isDateInPartialRange(date)
      const isStart = startDate && date.toDateString() === startDate.toDateString()
      const isEnd = endDate && date.toDateString() === endDate.toDateString()
      const isInRange = inRange && !isStart && !isEnd

      let bgColor = 'transparent'
      let textColor = isCurrentMonth ? '#212126' : '#9CA3AF'
      let borderRadius = '0px'

      if (isSelected) {
        bgColor = '#212126'
        textColor = '#FFFFFF'
        borderRadius = '6px'
      } else if (inRange || inPartialRange) {
        bgColor = '#F3F4F6'
        textColor = '#212126'
        if (isStart) {
          borderRadius = '6px 0px 0px 6px'
        } else if (isEnd) {
          borderRadius = '0px 6px 6px 0px'
        } else if (isInRange) {
          borderRadius = '0px'
        }
      }

      return (
        <button
          key={index}
          onClick={() => handleDateClick(date)}
          onMouseEnter={() => handleDateHover(date)}
          className="w-8 h-8 flex items-center justify-center text-sm transition-colors hover:bg-gray-100"
          style={{
            backgroundColor: bgColor,
            color: textColor,
            borderRadius: borderRadius,
            fontFamily: 'Lexend Deca',
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '20px',
            opacity: isCurrentMonth ? 1 : 0.5
          }}
        >
          {date.getDate()}
        </button>
      )
    })
  }

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-40"
        onClick={onClose}
      />
      
      {/* Calendar */}
      <div
        className="absolute z-50 bg-white rounded-lg shadow-lg flex flex-col"
        style={{
          width: '268px',
          height: '304px',
          border: '1px solid #E9E9EA',
          padding: '8px',
          top: '100%',
          left: 0,
          marginTop: '4px'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between" style={{ marginBottom: '16px' }}>
          <button
            onClick={getPreviousMonth}
            className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-100 transition-colors"
            style={{
              backgroundColor: '#F3F4F6'
            }}
          >
            <FiChevronLeft className="w-4 h-4 text-gray-600" />
          </button>
          
          <h3
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 600,
              fontSize: '14px',
              lineHeight: '20px',
              color: '#212126'
            }}
          >
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </h3>
          
          <button
            onClick={getNextMonth}
            className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-100 transition-colors"
            style={{
              backgroundColor: '#F3F4F6'
            }}
          >
            <FiChevronRight className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        {/* Days of Week */}
        <div className="grid grid-cols-7 gap-1" style={{ marginBottom: '8px' }}>
          {daysOfWeek.map((day) => (
            <div
              key={day}
              className="w-8 h-8 flex items-center justify-center text-xs"
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 400,
                fontSize: '12px',
                lineHeight: '16px',
                color: '#69697A'
              }}
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1">
          {renderCalendar()}
        </div>
      </div>
    </>
  )
}

export default RangeCalendar

